import React, {
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';
import {
	Box,
	Button,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Theme,
	Typography,
} from '@mui/material';
import BlockType from 'src/enums/blockType';
import { DeckContext } from '../../context/DeckContextV3';
import EdgeLabel from 'src/enums/edgeLabel';
import { toastError } from 'src/Toaster/Toast';
import handleAddStair from '../../utils/addStair';
import getEdgeFromString from 'src/utils/getEdge';
import CloseIcon from '@mui/icons-material/Close';
import handleAddLanding from '../../utils/addLanding';
import handleAddBumpout from '../../utils/addBumpout';
import handleAddDeckbox from '../../utils/addDeckbox';
import { getOppositeEdge } from '../../utils/edgeUtils';
import getBlock from 'src/utils/getBlockV3';
import getBlockChildren from 'src/utils/getBlockChildrenV3';

const BlockDestination: React.FC<{
	theme: Theme;
	setStage: Dispatch<SetStateAction<number>>;
	selectedBlockType: BlockType;
}> = ({ theme, setStage, selectedBlockType }) => {
	const { state, dispatch } = useContext(DeckContext);

	const [selectedBlockId, setSelectedBlockId] = useState<string | null>();
	const [selectedBlockEdgeLabel, setSelectedBlockEdgeLabel] =
		useState<EdgeLabel | null>();
	const [selectedBlockSegmentId, setSelectedBlockSegmentId] =
		useState<string>();

	const [addIntermidiateLanding, setAddIntermidiateLanding] = useState(false);
	const [addedStairId, setAddedStairId] = useState('');
	const [addedLandingId, setAddedLandingId] = useState('');

	const selectedBlock = getBlock(state.blocks, selectedBlockId || null);
	const selectedBlockEdge = selectedBlock?.edges.find(
		(edge) => edge.label == selectedBlockEdgeLabel
	);
	const selectedBlockSegment = selectedBlockEdge?.segments.find(
		(segment) => segment.id == selectedBlockSegmentId
	);

	const deckboxes = state.blocks.filter((blk) => blk.type == BlockType.DECKBOX);
	const landings = deckboxes.flatMap((dk) => dk.landings);
	const stairs = deckboxes.flatMap((dk) => dk.stairs);

	const options =
		selectedBlockType == BlockType.STAIR
			? [...deckboxes, ...landings]
			: selectedBlockType == BlockType.LANDING
				? [...deckboxes, ...stairs]
				: deckboxes;

	useEffect(() => {
		if (!state.blocks.length && selectedBlockType == BlockType.DECKBOX) {
			handleAddDeckbox({
				state,
				dispatch,
				block: selectedBlock,
				edge: selectedBlockEdge,
				segment: selectedBlockSegment,
			});
			setStage(0);
			return;
		}
	}, [state.blocks]);

	useEffect(() => {
		const addedStair = getBlock(state.blocks, addedStairId);

		if (addIntermidiateLanding && addedStair) {
			const blockAttachedEdge = addedStair.edges.find(
				(eg) => eg.attachedToParent
			);

			const edgeToAttachTo = addedStair.edges.find(
				(eg) => eg.label == getOppositeEdge(blockAttachedEdge?.label)
			);

			if (edgeToAttachTo) {
				const intermediateLandingId = handleAddLanding({
					state,
					dispatch,
					block: addedStair,
					edge: edgeToAttachTo,
					isIntermediate: true,
				});

				setAddedStairId('');
				setAddedLandingId(intermediateLandingId || '');
			}
		}
	}, [state.blocks, addedStairId, addIntermidiateLanding]);

	useEffect(() => {
		const addedLanding = getBlock(state.blocks, addedLandingId);

		if (addIntermidiateLanding && addedLanding) {
			const blockAttachedEdge = addedLanding.edges.find(
				(eg) => eg.attachedToParent
			);

			const edgeToAttachTo = addedLanding.edges.find(
				(eg) => eg.label == getOppositeEdge(blockAttachedEdge?.label)
			);

			if (edgeToAttachTo) {
				handleAddStair({
					state,
					dispatch,
					block: addedLanding,
					edge: edgeToAttachTo,
				});

				setAddIntermidiateLanding(false);
				setAddedLandingId('');
				setStage(0);
			}
		}
	}, [state.blocks, addedLandingId, addIntermidiateLanding]);

	const handleAddAssociation = () => {
		if (selectedBlockType === BlockType.DECKBOX) {
			handleAddDeckbox({
				state,
				dispatch,
				block: selectedBlock,
				edge: selectedBlockEdge,
				segment: selectedBlockSegment,
			});
			setStage(0);
			return;
		}

		if (!selectedBlockId) {
			toastError({
				title: 'Select a block type',
				description: `Please select a ${selectedBlockType == BlockType.STAIR ? 'Landing/DeckBox' : 'DeckBox'}.`,
			});

			return;
		}

		if (selectedBlock && selectedBlockEdge) {
			if (selectedBlockType === BlockType.BUMPOUT) {
				handleAddBumpout({
					dispatch,
					block: selectedBlock,
					edge: selectedBlockEdge,
				});
				setStage(0);
			} else if (selectedBlockType === BlockType.STAIR) {
				const result = handleAddStair({
					state,
					dispatch,
					block: selectedBlock,
					edge: selectedBlockEdge,
					segment: selectedBlockSegment,
				});

				if (result) {
					setAddIntermidiateLanding(result.addIntermediateLanding);
					setAddedStairId(result.addedStairId);
				}
			} else if (selectedBlockType === BlockType.LANDING) {
				handleAddLanding({
					state,
					dispatch,
					block: selectedBlock,
					edge: selectedBlockEdge,
					segment: selectedBlockSegment,
				});
				setStage(0);
			}
		}
	};
	return (
		<Box sx={{ padding: '5px', width: '350px' }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
					marginBottom: '10px',
				}}
			>
				<Typography sx={{ color: theme.palette.gray[500] }}>
					Add {selectedBlockType}
				</Typography>

				<IconButton onClick={() => setStage(0)}>
					<CloseIcon sx={{ color: theme.palette.grey[500] }} />
				</IconButton>
			</Box>

			<Stack sx={{ marginTop: 2, marginBottom: 2 }}>
				<InputLabel>
					Select
					{selectedBlockType == BlockType.STAIR
						? 'Landing/'
						: selectedBlockType == BlockType.LANDING
							? 'Stair/'
							: ''}
					DeckBox
				</InputLabel>
				<Select
					id="selectBlockDestination"
					value={selectedBlockId}
					variant="outlined"
					onChange={(e) => setSelectedBlockId(e.target.value)}
					fullWidth
				>
					{options.map((block, index) => (
						<MenuItem key={index} value={block?.id || ''}>
							{block?.label}
						</MenuItem>
					))}
				</Select>
			</Stack>

			{selectedBlockId && (
				<Stack sx={{ marginTop: 2, marginBottom: 2 }}>
					<InputLabel>Select Edge</InputLabel>
					<Select
						id="selectBlockEdge"
						value={selectedBlockEdgeLabel}
						variant="outlined"
						onChange={(e) => {
							setSelectedBlockEdgeLabel(getEdgeFromString(e.target.value));
						}}
						fullWidth
					>
						{['Top', 'Right', 'Bottom', 'Left'].map((side) => {
							const selectedBlock = getBlock(state.blocks, selectedBlockId);
							const parentAttachedEdge = selectedBlock?.edges.find(
								(edge) => edge.attachedToParent
							);

							const sideEdgeLabels = selectedBlock?.edges
								?.filter(
									(edge) =>
										edge.label != parentAttachedEdge?.label &&
										edge.label != getOppositeEdge(parentAttachedEdge?.label)
								)
								?.map((edge) => edge.label);

							let disabled = false;

							if (selectedBlock?.type == BlockType.STAIR) {
								disabled = Boolean(sideEdgeLabels?.includes(side));
							}

							return (
								<MenuItem disabled={disabled} key={side} value={side}>
									{side}
								</MenuItem>
							);
						})}
					</Select>
				</Stack>
			)}

			{selectedBlockId &&
				selectedBlockEdge &&
				selectedBlockType != BlockType.BUMPOUT && (
					<Stack sx={{ marginTop: 2, marginBottom: 2 }}>
						<InputLabel sx={{ marginBottom: '0px' }}>
							Select Segment (Optional)
						</InputLabel>

						<Typography
							sx={{ fontSize: '12px', marginY: '2px', color: 'orange' }}
						>
							If no segment is selected, one will be created automatically
						</Typography>

						<Select
							id="selectBlockSegment"
							value={selectedBlockSegmentId}
							variant="outlined"
							onChange={(e) => {
								setSelectedBlockSegmentId(e.target.value);
							}}
							fullWidth
						>
							{selectedBlockEdge.segments.map((segment, index) => {
								let disabled = false;

								if (selectedBlock) {
									const blockChildren = getBlockChildren(
										state.blocks,
										selectedBlock
									);

									const childBlockAttachedToSegment = blockChildren.findIndex(
										(child) => child.parentBlock.segmentId == segment.id
									);

									disabled = childBlockAttachedToSegment > -1;
								}

								return (
									<MenuItem
										disabled={disabled}
										key={segment.id}
										value={segment.id}
									>
										Segment {index + 1}
									</MenuItem>
								);
							})}
						</Select>
					</Stack>
				)}

			<Button
				onClick={handleAddAssociation}
				disabled={
					selectedBlockType != BlockType.DECKBOX &&
					(state.blocks.filter((blk) => blk.type === BlockType.DECKBOX)
						.length === 0 ||
						!selectedBlockId ||
						(selectedBlockType !== BlockType.BUMPOUT && !selectedBlockEdge))
				}
				variant="contained"
			>
				Add
			</Button>
		</Box>
	);
};

export default BlockDestination;
